import {
  VENDOR_FILTERS,
  type ResponseOf,
  type VendorRow,
  type VendorTotals,
} from "@checkone/contract/client";
import { useEffect, useId, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useApi } from "../../api/hooks";
import { RateCell } from "../../components/RateCell";
import { StatusPill } from "../../components/StatusPill";
import { Empty, ErrorState, Loading, PageHeader } from "../../components/ui";
import { formatDayMonth, formatDayShort } from "../../lib/format";
import { useCheckingPoll } from "./checking";
import { EditVendorDialog } from "./EditVendorDialog";
import { ExportVendorList } from "./ExportVendorList";
import { NeedsIdentifierRow } from "./NeedsIdentifier";
import { Onboarding } from "./Onboarding";
import { PayeeTypeCell } from "./PayeeTypeCell";

type Filter = (typeof VENDOR_FILTERS)[number];

const FILTER_LABEL: Record<Filter, string> = {
  all: "All",
  changed: "Changed this week",
  not_on_atl: "Not on ATL",
  not_on_st: "Not on sales-tax ATL",
  needs_identifier: "Needs identifier",
  payee_unconfirmed: "Confirm payee type",
  inactive: "Inactive",
};

const ISSUE_LABEL: Record<string, string> = {
  ntn_cnic_disagree: "NTN and CNIC disagree",
  malformed: "Identifier not readable",
  duplicate: "Identifier used twice",
};

export function VendorsPage() {
  const [params, setParams] = useSearchParams();
  const status = (VENDOR_FILTERS as readonly string[]).includes(params.get("status") ?? "")
    ? (params.get("status") as Filter)
    : "all";
  const q = params.get("q") ?? "";
  const [search, setSearch] = useState(q);
  const [cursors, setCursors] = useState<string[]>([]);
  const [editing, setEditing] = useState<VendorRow | null>(null);
  const searchId = useId();
  const cursor = cursors[cursors.length - 1];

  useEffect(() => setCursors([]), [status, q]);

  // A vendor just added or given a new NTN or CNIC shows "Checking…" until its check lands.
  const poll = useCheckingPoll<ResponseOf<"vendors.list">>((d) => d.items);
  const list = useApi(
    "vendors.list",
    { query: { status, q: q || undefined, cursor, limit: 50, changed_since: undefined } },
    { refetchInterval: poll },
  );

  const setFilter = (next: Filter) => {
    const p = new URLSearchParams(params);
    if (next === "all") p.delete("status");
    else p.set("status", next);
    setParams(p, { replace: true });
  };

  // First visit: nothing uploaded yet, so the page is the upload prompt (no empty table, no filters).
  if (list.data && list.data.totals.all === 0 && list.data.totals.inactive === 0 && status === "all" && !q)
    return <Onboarding />;

  const totals = list.data?.totals;
  const asOf = list.data?.as_of;
  const it = asOf?.files.find((f) => f.source === "ATL_IT");
  const st = asOf?.files.find((f) => f.source === "ATL_ST");

  return (
    <>
      <PageHeader
        title={totals ? `${totals.all} ${totals.all === 1 ? "vendor" : "vendors"}` : "Vendors"}
        subtitle={
          asOf && totals ? (
            <>
              Checked against FBR files of {formatDayShort(it?.published_on)} (income tax) and{" "}
              {formatDayShort(st?.published_on)} (sales tax) · {totals.not_on_atl} not on income-tax ATL ·{" "}
              {totals.not_on_st} not on sales-tax ATL
              {totals.needs_identifier > 0 && <> · {totals.needs_identifier} need an identifier</>}
              {asOf.rate_table && <> · rates from table v{asOf.rate_table.version}</>}
            </>
          ) : (
            "Your vendor list and each vendor's FBR status."
          )
        }
        actions={
          <>
            <ExportVendorList />
            <Link className="btn primary" to="/upload">
              Upload new list
            </Link>
          </>
        }
      />

      {totals && totals.needs_identifier > 0 && status !== "needs_identifier" && (
        <div className="callout warn spread" style={{ marginBottom: "var(--space-4)" }}>
          <span>
            <strong>
              {totals.needs_identifier === 1
                ? "1 vendor needs an identifier"
                : `${totals.needs_identifier} vendors need an identifier`}
            </strong>{" "}
            and {totals.needs_identifier === 1 ? "is" : "are"} not checked. We can suggest matches by name;
            you confirm each one.
          </span>
          <button className="btn small" onClick={() => setFilter("needs_identifier")}>
            Review them
          </button>
        </div>
      )}

      <div className="stack" style={{ marginBottom: "1.25rem", gap: "var(--space-3)" }}>
        <form
          className="row"
          role="search"
          style={{ gap: "var(--space-2)" }}
          onSubmit={(e) => {
            e.preventDefault();
            const p = new URLSearchParams(params);
            if (search.trim()) p.set("q", search.trim());
            else p.delete("q");
            setParams(p, { replace: true });
          }}
        >
          <label htmlFor={searchId} className="sr-only">
            Search vendors
          </label>
          <input
            id={searchId}
            type="search"
            placeholder="Search name, NTN, STRN or last CNIC digits"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: "1 1 16rem", width: "auto" }}
          />
          <button className="btn" type="submit">
            Search
          </button>
        </form>
        <div className="segmented" role="group" aria-label="Filter vendors">
          {VENDOR_FILTERS.map((f) => (
            <button key={f} className="chip" aria-pressed={status === f} onClick={() => setFilter(f)}>
              {FILTER_LABEL[f]}
              {totals && f !== "all" && <span className="count">{totals[f]}</span>}
            </button>
          ))}
        </div>
      </div>

      {list.isPending && <Loading label="Loading vendors…" />}
      {list.error && <ErrorState error={list.error} retry={() => void list.refetch()} />}
      {list.data && list.data.items.length === 0 && (
        <Empty>
          {status === "all" && !q ? (
            <>
              No active vendors. <Link to="/upload">Upload your vendor list</Link> to start.
            </>
          ) : (
            "No vendors match this filter."
          )}
        </Empty>
      )}
      {list.data && list.data.items.length > 0 && (
        <>
          <div className="table-wrap responsive">
            <table className="data responsive">
              <caption className="sr-only">Vendors and their FBR status ({FILTER_LABEL[status]})</caption>
              <thead>
                <tr>
                  <th scope="col">Vendor</th>
                  <th scope="col">Identifier</th>
                  <th scope="col">Income-tax ATL</th>
                  <th scope="col">Sales-tax ATL</th>
                  <th scope="col">Changed</th>
                  <th scope="col" className="num">
                    Withholding rate
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.data.items.map((v) =>
                  v.needs_identifier && status === "needs_identifier" ? (
                    <NeedsIdentifierRow key={v.id} vendor={v} onEdit={() => setEditing(v)} />
                  ) : (
                    <VendorTableRow key={v.id} vendor={v} />
                  ),
                )}
              </tbody>
            </table>
          </div>
          <div className="spread" style={{ marginTop: "var(--space-3)" }}>
            <span className="subtle">
              Showing {list.data.items.length} of{" "}
              {status === "all" ? list.data.totals.all : list.data.totals[status as keyof VendorTotals]}.
              Names are as you uploaded them; matching uses identifiers only.
            </span>
            <div className="row" style={{ gap: "var(--space-2)" }}>
              <button
                className="btn small"
                disabled={cursors.length === 0}
                onClick={() => setCursors((c) => c.slice(0, -1))}
              >
                Previous
              </button>
              <button
                className="btn small"
                disabled={!list.data.next_cursor}
                onClick={() => list.data.next_cursor && setCursors((c) => [...c, list.data.next_cursor!])}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
      <EditVendorDialog vendor={editing} onClose={() => setEditing(null)} />
    </>
  );
}

/** NTN, masked CNIC and STRN as stored; STRN is kept for records and never matched. */
export function Identifiers({ v }: { v: Pick<VendorRow, "ntn" | "cnic_masked" | "strn"> }) {
  const parts = [
    v.ntn && `NTN ${v.ntn}`,
    v.cnic_masked && `CNIC ${v.cnic_masked}`,
    v.strn && `STRN ${v.strn}`,
  ].filter((p): p is string => Boolean(p));
  if (parts.length === 0) return <span className="muted">No identifier</span>;
  return (
    <>
      {parts.map((p) => (
        <div key={p} className="nowrap">
          {p}
        </div>
      ))}
    </>
  );
}

/** "Dropped off income-tax ATL, 18 Oct": the latest list change, in words. */
export function changeText(c: NonNullable<VendorRow["last_change"]>): string {
  const what = c.change === "added" ? "Re-entered" : "Dropped off";
  const list = c.source === "ATL_IT" ? "income-tax ATL" : "sales-tax ATL";
  return `${what} ${list}, ${formatDayMonth(c.detected_at)}`;
}

function VendorTableRow({ vendor: v }: { vendor: VendorRow }) {
  return (
    <tr>
      <td data-label="">
        <Link to={`/vendors/${v.id}`} className="vendor-name">
          {v.display_name}
        </Link>
        {!v.active && (
          <div>
            <span className="badge">Inactive</span>
          </div>
        )}
      </td>
      <td data-label="Identifier" className="mono">
        <Identifiers v={v} />
      </td>
      <td data-label="Income-tax ATL">
        <StatusPill source="ATL_IT" result={v.lists.ATL_IT} compact />
      </td>
      <td data-label="Sales-tax ATL">
        <StatusPill source="ATL_ST" result={v.lists.ATL_ST} compact />
      </td>
      <td data-label="Changed" className="text-2">
        {v.last_change ? (
          changeText(v.last_change)
        ) : v.identifier_issue ? (
          <span className="muted">{ISSUE_LABEL[v.identifier_issue]}</span>
        ) : (
          <span className="muted">—</span>
        )}
      </td>
      <td data-label="Rate" className="num">
        <RateCell rate={v.rate} showPayee={v.payee_type !== "unconfirmed"} />
        {v.payee_type === "unconfirmed" && (
          <div style={{ marginTop: "var(--space-2)" }}>
            <PayeeTypeCell vendor={v} />
          </div>
        )}
      </td>
    </tr>
  );
}
